import { DatabaseZap, BookOpen, Sparkles, PenLine, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DdqSessionQuestion } from '@/types/api.types';
import { getTierColors, getTierLabel } from '../utils/tierColors';

export interface QueueRailProps {
  questions: DdqSessionQuestion[];
  activeId: string | null;
  onSelect: (questionId: string) => void;
  className?: string;
}

const SOURCE_ICON = {
  vault_data: DatabaseZap,
  knowledge_base: BookOpen,
  llm: Sparkles,
  firm_required: PenLine,
} as const;

function ProvenanceIcon({ source }: { source: DdqSessionQuestion['answer_source'] }) {
  if (!source || !(source in SOURCE_ICON)) {
    return <PenLine className="h-3.5 w-3.5 text-muted-foreground/70" aria-hidden="true" />;
  }
  const Icon = SOURCE_ICON[source];
  const isVault = source === 'vault_data';
  return (
    <Icon
      className={cn(
        'h-3.5 w-3.5',
        isVault ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground/70',
      )}
      aria-hidden="true"
    />
  );
}

/**
 * The cockpit queue: a dense, scannable list of questions. Each row shows a
 * tier-coloured confidence dot, the question number, the (truncated) question,
 * and a provenance icon. Selecting a row sets the active cursor that drives the
 * evidence pane. Implemented as an ARIA listbox so keyboard and screen-reader
 * users track the active option.
 */
export function QueueRail({ questions, activeId, onSelect, className }: QueueRailProps) {
  return (
    <div
      role="listbox"
      aria-label="Questions"
      aria-activedescendant={activeId ? `queue-row-${activeId}` : undefined}
      className={cn('border border-border rounded-lg overflow-hidden divide-y divide-border', className)}
    >
      {questions.map((q) => {
        const isActive = q.question_id === activeId;
        const tier = getTierColors(q.response_tier);
        const actioned = q.userAction && q.userAction !== 'Pending';
        return (
          <button
            key={q.question_id}
            id={`queue-row-${q.question_id}`}
            type="button"
            role="option"
            aria-selected={isActive}
            onClick={() => onSelect(q.question_id)}
            className={cn(
              'w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors',
              isActive ? 'bg-muted' : 'hover:bg-muted/50',
            )}
          >
            <span
              className={cn('shrink-0 h-2 w-2 rounded-full', tier.bg)}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground">
                <span>Q{q.index + 1}</span>
                <span aria-hidden="true">·</span>
                <span>{getTierLabel(q.response_tier)}</span>
              </div>
              <p
                className={cn(
                  'text-sm truncate',
                  isActive ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {q.incoming_question}
              </p>
            </div>
            {actioned ? (
              <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" aria-hidden="true" />
            ) : (
              <span className="shrink-0">
                <ProvenanceIcon source={q.answer_source} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
